import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { CORTOLoader } from "./CORTOLoader";
import { sharedData, VIEWS, VISIBILITY_MODES } from "./model-state";
import cloneDeep from "lodash.clonedeep";

import * as ProgressBar from "progressbar.js";
import axios from "axios";
import JSZip from "jszip";
import canvasManager from "./canvas-manager";
import { isPortrait } from "./scene-utils";
import {
  registerTooth,
  initializeIPRVisualization,
  findTeethBySearch,
  updateIPRPositions,
  setIprLayers,
  setMissingTeethFDI,
  getIPRStartAlignerNumbers,
  getAttachmentLayerMarkers
} from "./ipr-visualization";
import { setMovementTableData } from "./movement-table";
const progressBar = new ProgressBar.Circle("#loader", {
  strokeWidth: 4,
  color: "#088cfe",
  text: {
    value: "1%",
    className: "progressbar-label",
  },
});

const loadingManager = new THREE.LoadingManager();

const { gums, teeth, attachments } = sharedData.plyFiles;

let upperActiveNum = "";
let lowerActiveNum = "";

/**
 * Get the current active aligner numbers
 * @returns {{upperActiveNum: string, lowerActiveNum: string}}
 */
export const getActiveAlignerNumbers = () => {
  return { upperActiveNum, lowerActiveNum };
};

const TOOTH_DEFAULT_COLOR = '#FFFFF8';
const TOOTH_HIGHLIGHT_COLOR = '#6ECFD4';
let selectedTooth = null; // { jaw, toothIdx }

const applyToothHighlightToCanvas = (canvasId, jaw, toothIdx, highlighted) => {
  const data = canvasManager.get(canvasId);
  if (!data?.models?.[jaw]) return;
  Object.values(data.models[jaw]).forEach((aligner) => {
    const mat = aligner.teeth?.[toothIdx];
    if (!mat) return;
    mat.color.set(highlighted ? TOOTH_HIGHLIGHT_COLOR : TOOTH_DEFAULT_COLOR);
    mat.userData.isHighlighted = highlighted;
  });
};

export const applyHighlightToAllCanvases = (jaw, toothIdx, highlighted) => {
  canvasManager.getAll().forEach(({ id }) => {
    applyToothHighlightToCanvas(id, jaw, toothIdx, highlighted);
  });
};

export const selectTooth = (jaw, toothIdx) => {
  const isSame = selectedTooth?.jaw === jaw && selectedTooth?.toothIdx === toothIdx;
  if (selectedTooth) {
    applyHighlightToAllCanvases(selectedTooth.jaw, selectedTooth.toothIdx, false);
    selectedTooth = null;
  }
  if (!isSame) {
    selectedTooth = { jaw, toothIdx };
    applyHighlightToAllCanvases(jaw, toothIdx, true);
  }
};

export const deselectTooth = () => {
  if (!selectedTooth) return;
  applyHighlightToAllCanvases(selectedTooth.jaw, selectedTooth.toothIdx, false);
  selectedTooth = null;
};

let _isCompressed = false;
const generatedModelUrls = [];
const modelUrlMeta = new Map();
let _preModelProgress = 0;

const normalizeAlignerNum = (rawNum) => {
  if (rawNum == null) return null;
  const normalized = String(rawNum).trim();
  const numericPrefix = normalized.match(/^\d+/)?.[0];
  if (!numericPrefix) return normalized;
  return String(parseInt(numericPrefix, 10)).padStart(2, "0");
};

const parseModelMetaFromPath = (filePath) => {
  const normalized = decodeURIComponent(filePath).replaceAll("\\", "/");
  const parts = normalized.split("/").filter(Boolean);
  const typeIdx = parts.findIndex((part) => {
    const value = part.toLowerCase();
    return value === "upper" || value === "lower";
  });

  if (typeIdx === -1 || !parts[typeIdx + 1]) return null;

  return {
    type: parts[typeIdx].toUpperCase(),
    num: normalizeAlignerNum(parts[typeIdx + 1]),
  };
};

const getModelMeta = (modelUrl) => {
  const fromZip = modelUrlMeta.get(modelUrl);
  if (fromZip) return fromZip;
  return parseModelMetaFromPath(modelUrl);
};

const addFileToBuckets = (fileUrl, fileName) => {
  const lowerCaseName = fileName.toLowerCase();
  if (lowerCaseName.includes("template")) return;
  if (lowerCaseName.includes("attachment")) attachments.push(fileUrl);
  if (lowerCaseName.includes("tooth")) teeth.push(fileUrl);
  if (lowerCaseName.includes("gum")) gums.push(fileUrl);
};

const extractModelsFromZip = async (
  zipFileUrl,
  onDownloadProgress,
  onExtractProgress
) => {
  let bestKnownDownloadRatio = 0;
  let hasExactProgress = false;
  let lastProgressEventAt = Date.now();
  const reportDownloadProgress = (ratio) => {
    const clamped = Math.max(0, Math.min(1, ratio));
    if (clamped < bestKnownDownloadRatio) return;
    bestKnownDownloadRatio = clamped;
    onDownloadProgress?.(bestKnownDownloadRatio);
  };

  // Fallback only when total size is unknown and events are sparse.
  const fallbackTimer = setInterval(() => {
    if (hasExactProgress) return;
    if (bestKnownDownloadRatio >= 0.95) return;
    const elapsedSinceEvent = Date.now() - lastProgressEventAt;
    if (elapsedSinceEvent < 1200) return;
    reportDownloadProgress(bestKnownDownloadRatio + 0.005);
  }, 450);

  const { data: zipBuffer } = await axios.get(zipFileUrl, {
    responseType: "arraybuffer",
    onDownloadProgress: (e) => {
      lastProgressEventAt = Date.now();
      if (e.total) {
        hasExactProgress = true;
        reportDownloadProgress(e.loaded / e.total);
      } else if (e.loaded) {
        // No total available (chunked encoding): conservative estimate to avoid
        // getting far ahead and stalling at a high percentage.
        reportDownloadProgress(
          Math.min(0.95, 0.02 + (e.loaded / (50 * 1024 * 1024)) * 0.93)
        );
      }
    },
  });
  clearInterval(fallbackTimer);
  reportDownloadProgress(1);
  const zip = await JSZip.loadAsync(zipBuffer);

  const extractedFiles = [];
  let hasCompressedFiles = false;
  const zipEntries = Object.keys(zip.files);
  let processedEntries = 0;

  for (const entryName of zipEntries) {
    const entry = zip.files[entryName];
    processedEntries++;
    if (entry.dir) continue;

    const lowerCaseName = entry.name.toLowerCase();
    if (!lowerCaseName.endsWith(".crt") && !lowerCaseName.endsWith(".ply")) {
      continue;
    }

    const modelMeta = parseModelMetaFromPath(entry.name);
    if (!modelMeta) continue;

    const fileBuffer = await entry.async("arraybuffer");
    const objectUrl = URL.createObjectURL(
      new Blob([fileBuffer], { type: "application/octet-stream" })
    );

    generatedModelUrls.push(objectUrl);
    modelUrlMeta.set(objectUrl, {
      ...modelMeta,
      sourceName: entry.name,
    });

    extractedFiles.push({ url: objectUrl, name: lowerCaseName });
    if (lowerCaseName.endsWith(".crt")) hasCompressedFiles = true;
    if (zipEntries.length) {
      onExtractProgress?.(processedEntries / zipEntries.length);
    }
  }
  onExtractProgress?.(1);

  return { extractedFiles, hasCompressedFiles };
};

const cleanupGeneratedModelUrls = () => {
  generatedModelUrls.forEach((url) => URL.revokeObjectURL(url));
  generatedModelUrls.length = 0;
  modelUrlMeta.clear();
};

const ZIP_DOWNLOAD_START = 0.01; // 1%
const ZIP_DOWNLOAD_END = 0.75; // 75%
const ZIP_EXTRACT_WEIGHT = 0.15; // 75–90% for zip extraction

/**
 * Union of missing FDI numbers from API root and from each treatment layer.
 * N-layers / PDF extraction uses per-layer `missingTooth`; older payloads use top-level `missingTeeth`.
 */
const collectMissingTeethFromLayersPayload = (missingTeethRoot, layers) => {
  const missingSet = new Set();
  if (Array.isArray(missingTeethRoot)) {
    for (const n of missingTeethRoot) {
      const v = parseInt(n, 10);
      if (!Number.isNaN(v)) missingSet.add(v);
    }
  }
  if (layers && typeof layers === "object") {
    for (const layer of Object.values(layers)) {
      const arr = layer?.missingTooth ?? layer?.missingTeeth;
      if (Array.isArray(arr)) {
        for (const n of arr) {
          const v = parseInt(n, 10);
          if (!Number.isNaN(v)) missingSet.add(v);
        }
      }
    }
  }
  return [...missingSet];
};

const fetchFiles = async (onZipDownloadProgress, onZipExtractProgress) => {
  // progressBar.destroy();
  // document.getElementById("loader-container").style.display = "none";
  // return;

  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id");

  const { data } = await axios.get(
    `${process.env.API_PREFIX}/ply-files?id=${id}`
  );

  const {
    plyFiles: files = [],
    zipFile,
    isCompressed,
    pdfLinks,
    layers,
    patientName,
    movementTable,
  } = data.data;

  if (isCompressed) _isCompressed = isCompressed;
  const mergedMissing = collectMissingTeethFromLayersPayload(
    data.data.missingTeeth,
    layers
  );
  setIprLayers(layers);
  setMissingTeethFDI(mergedMissing);
  setMovementTableData(movementTable);

  document.body.dataset.customerId = data.data.mobileOrUid;
  document.getElementById("customerId").textContent = data.data.mobileOrUid;
  if (data.data.mobileOrUid) document.title = data.data.mobileOrUid;
  if (data.data.planName) document.getElementById("planName").textContent = data.data.planName;
  if (data.data.planNo) document.getElementById("planNo").textContent = `Design No: ${data.data.planNo}`;
  const patientNameEl = document.getElementById("patientName");
  if (patientNameEl && patientName) patientNameEl.textContent = patientName;

  let hasFilesFromZip = false;
  if (zipFile && data?.data?.isCompressed) {
    try {
      const { extractedFiles, hasCompressedFiles } =
        await extractModelsFromZip(
          zipFile,
          onZipDownloadProgress,
          onZipExtractProgress
        );
      extractedFiles.forEach((file) => addFileToBuckets(file.url, file.name));
      hasFilesFromZip = extractedFiles.length > 0;
      if (hasCompressedFiles) _isCompressed = true;
    } catch (err) {
      console.log("Failed to extract zip file", err);
    }
  }

  if (!hasFilesFromZip) {
    files.forEach((file) => addFileToBuckets(file, file));
  }

  updatePdfLinkModalContent(pdfLinks ?? {});
};

const addLoadedModelsToScene = (canvasId, scene) => {
  if (!sharedData.areFilesLoaded || !sharedData.modelsObjectCache.length) {
    return;
  }

  const defaultCanvasId = "canvas";

  const { models: defaultModels, modelGeometries: defaultModelGeometries } =
    canvasManager.get(defaultCanvasId);

  // Build a fresh structure so each aligner index has its own object. Using
  // cloneDeep(defaultModels) would make filled indices (e.g. UPPER["03"], ["04"])
  // share the same object as the last real index (["02"]), so assigning .gumMesh
  // per key would overwrite the shared object and leave orphan meshes in the scene.
  const newModels = { UPPER: {}, LOWER: {} };
  const newSuperImpositions = {};

  Object.entries(defaultModels).forEach(([jaw, aligners]) => {
    Object.entries(aligners).forEach(([alignerNum, parts]) => {
      const slot = {};
      newModels[jaw][alignerNum] = slot;

      if (parts.gum) {
        const clonedMaterial = parts.gum.clone();
        clonedMaterial.visible = false;
        const gumMesh = new THREE.Mesh(
          defaultModelGeometries[jaw][alignerNum].gum.clone(),
          clonedMaterial
        );
        gumMesh.visible = false;
        scene.add(gumMesh);
        slot.gum = clonedMaterial;
        slot.gumMesh = gumMesh;
      }

      if (parts.teeth) {
        slot.teeth = [];
        parts.teeth.forEach((tooth, idx) => {
          const clonedMaterial = tooth.clone();
          clonedMaterial.visible = false;
          const toothMesh = new THREE.Mesh(
            defaultModelGeometries[jaw][alignerNum]["teeth"][idx].clone(),
            clonedMaterial
          );
          toothMesh.userData.type = 'tooth';
          toothMesh.userData.toothId = { jaw, toothIdx: idx };
          scene.add(toothMesh);
          slot.teeth[idx] = clonedMaterial;
          if (alignerNum === "00") {
            if (!newSuperImpositions[jaw]) newSuperImpositions[jaw] = [];
            newSuperImpositions[jaw].push(clonedMaterial);
          }
        });
      }

      if (parts.attachments) {
        slot.attachments = [];
        parts.attachments.forEach((attachment, idx) => {
          const clonedMaterial = attachment.clone();
          clonedMaterial.visible = false;
          scene.add(
            new THREE.Mesh(
              defaultModelGeometries[jaw][alignerNum]["attachments"][
                idx
              ].clone(),
              clonedMaterial
            )
          );
          slot.attachments[idx] = clonedMaterial;
        });
      }
    });
  });

  canvasManager.update(canvasId, {
    models: newModels,
    superImpositions: newSuperImpositions,
  });

  // Re-apply tooth highlight to this new canvas if one is already selected
  if (selectedTooth) {
    applyToothHighlightToCanvas(canvasId, selectedTooth.jaw, selectedTooth.toothIdx, true);
  }
};

export const loadModels = async (canvasId, scene) => {
  if (sharedData.areFilesLoaded) {
    addLoadedModelsToScene(canvasId, scene);
    return;
  }
  _preModelProgress = 0;
  await _loadModels(canvasId, scene, (itemsLoaded, itemsTotal) => {
    if (sharedData.areFilesLoaded) {
      return;
    }
    const loadRatio = itemsLoaded / itemsTotal;
    const overallProgress =
      _preModelProgress + loadRatio * (1 - _preModelProgress);
    progressBar.animate(overallProgress, { duration: 300 });
    progressBar.setText(`${Math.ceil(overallProgress * 100)}%`);
  });
};

const _loadModels = async (canvasId, scene, onProgress) => {
  if (!sharedData.areFilesLoaded) {
    const onZipDownloadProgress = (ratio) => {
      if (sharedData.areFilesLoaded) return;
      _preModelProgress =
        ZIP_DOWNLOAD_START + ratio * (ZIP_DOWNLOAD_END - ZIP_DOWNLOAD_START);
      progressBar.animate(_preModelProgress, { duration: 150 });
      progressBar.setText(`${Math.ceil(_preModelProgress * 100)}%`);
    };
    const onZipExtractProgress = (ratio) => {
      if (sharedData.areFilesLoaded) return;
      _preModelProgress = ZIP_DOWNLOAD_END + ratio * ZIP_EXTRACT_WEIGHT;
      progressBar.animate(_preModelProgress, { duration: 120 });
      progressBar.setText(`${Math.ceil(_preModelProgress * 100)}%`);
    };
    await fetchFiles(onZipDownloadProgress, onZipExtractProgress);
  }
  const addToothModelToScene = (geometry) => {
    geometry.computeVertexNormals();
    const material = new THREE.MeshPhysicalMaterial({
      color: "#FFFFF8",
      roughness: 0.15,
      metalness: 0,
      clearcoat: 0.8,
      clearcoatRoughness: 0.1,
      reflectivity: 0.9,
      envMapIntensity: 1.2,
      side: THREE.DoubleSide,
      // transmission: 0.1, // Slight translucency
      thickness: 1.0,
      ior: 1.5,
    });
    // material.flatShading = false;
    // material.side = THREE.DoubleSide;
    material.visible = false;
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData.type = 'tooth';
    // mesh.doubleSided = true;
    // mesh.castShadow = true;
    // mesh.receiveShadow = true;
    scene.add(mesh);
    sharedData.modelsObjectCache.push(mesh.clone(true));
    return { geometry, material, mesh };
  };

  const addAttachmentModelToScene = (geometry) => {
    geometry.computeVertexNormals();
    const material = new THREE.MeshPhysicalMaterial({ color: "#e57f82" });
    material.flatShading = false;
    material.side = THREE.DoubleSide;
    material.visible = false;
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);
    sharedData.modelsObjectCache.push(mesh.clone(true));
    return { geometry, material };
  };

  const addGumModelToScene = (geometry) => {
    geometry.computeVertexNormals();
    const material = new THREE.MeshPhysicalMaterial({
      color: "#e57f82",
      roughness: 0.35,
      metalness: 0,
      clearcoat: 0.3,
      clearcoatRoughness: 0.2,
      reflectivity: 0.5,
      envMapIntensity: 0.8,
      side: THREE.DoubleSide,
    });
    material.flatShading = false;
    material.side = THREE.DoubleSide;
    material.visible = false;
    const mesh = new THREE.Mesh(geometry, material);
    mesh.doubleSided = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);
    sharedData.modelsObjectCache.push(mesh.clone(true));
    return { geometry, material };
  };

  const loader = _isCompressed
    ? new CORTOLoader(loadingManager)
    : new PLYLoader(loadingManager);

  const addMaterial = (canvasId, modelUrl, material, modelType, geometry) => {
    let { models, modelGeometries } = canvasManager.get(canvasId);
    if (!models) models = {};
    if (!modelGeometries) modelGeometries = {};

    if (modelUrl.includes("segmented"))
      modelUrl = modelUrl.replace("%20segmented", "");
    const modelMeta = getModelMeta(modelUrl);
    const type = modelMeta?.type;
    const num = modelMeta?.num;
    if (!type || !num) return;
    if (!models[type]) models[type] = {};
    if (!modelGeometries[type]) modelGeometries[type] = {};

    if (!models[type][num]) models[type][num] = {};
    if (!modelGeometries[type][num]) modelGeometries[type][num] = {};

    if (modelType === "gum") {
      models[type][num][modelType] = material;
      modelGeometries[type][num][modelType] = geometry;
      canvasManager.update(canvasId, { models, modelGeometries });
      return;
    }

    if (!models[type][num]?.[modelType]) models[type][num][modelType] = [];
    if (!modelGeometries[type][num]?.[modelType])
      modelGeometries[type][num][modelType] = [];

    models[type][num][modelType].push(material);
    modelGeometries[type][num][modelType].push(geometry);
    canvasManager.update(canvasId, { models, modelGeometries });
  };

  let itemsLoaded = 0;
  const itemsTotal = teeth.length + gums.length + attachments.length;

  const chunkSize = 12;

  for (let i = 0; i < teeth.length; i = i + chunkSize) {
    const teethModelChunk = [];
    const teethModelPromise = [];
    for (let j = i; j < teeth.length && j < i + chunkSize; j++) {
      teethModelChunk.push(teeth[j]);
      teethModelPromise.push(loader.loadAsync(teeth[j]));
      itemsLoaded++;
    }

    const teethGeometry = await Promise.all(teethModelPromise);

    teethGeometry.forEach((geometry, idx) => {
      const { geometry: _geometry, material, mesh } = addToothModelToScene(geometry);
      const modelUrl = teethModelChunk[idx];
      addMaterial(canvasId, modelUrl, material, "teeth", _geometry);

      // Extract jaw and aligner number from URL for tooth registration
      const modelMeta = getModelMeta(modelUrl);
      const jaw = modelMeta?.type;
      const alignerNum = modelMeta?.num;
      const sourceNameForToothMapping = modelMeta?.sourceName || modelUrl;

      // Tag mesh with identity so raycasting can identify the tooth.
      // Read the index from the materials array that addMaterial just pushed
      // into — without a fallback, so a missing array surfaces as a visible
      // bug rather than silently colliding with tooth index 0.
      if (jaw && alignerNum) {
        const { models } = canvasManager.get(canvasId);
        const teethArr = models?.[jaw]?.[alignerNum]?.teeth;
        if (Array.isArray(teethArr) && teethArr.length > 0) {
          mesh.userData.toothId = { jaw, toothIdx: teethArr.length - 1 };
        }
      }

      // Register tooth for IPR visualization - now including the mesh
      if (jaw && alignerNum) {
        registerTooth(
          sourceNameForToothMapping,
          mesh,
          _geometry,
          jaw,
          alignerNum,
          idx
        );
      }
    });

    onProgress(itemsLoaded, itemsTotal);
    // await sleep();
  }
  // return;

  // teeth.forEach((model) => {
  //   loader.load(model, (geometry) => {
  //     const material = addToothModelToScene(geometry);
  //     addMaterial(model, material, "teeth");
  //   });
  // });
  for (let i = 0; i < gums.length; i = i + chunkSize) {
    const gumModelsPromise = [];
    const gumsModelChunk = [];
    for (let j = i; j < gums.length && j < i + chunkSize; j++) {
      gumsModelChunk.push(gums[j]);
      gumModelsPromise.push(loader.loadAsync(gums[j]));
      itemsLoaded++;
    }

    const gumsGeometry = await Promise.all(gumModelsPromise);

    gumsGeometry.forEach((geometry, i) => {
      const { geometry: _geometry, material } = addGumModelToScene(geometry);
      addMaterial(canvasId, gumsModelChunk[i], material, "gum", _geometry);
    });

    onProgress(itemsLoaded, itemsTotal);
  }

  // gums.forEach((gumModel, idx) => {
  //   loader.load(gumModel, (geometry) => {
  //     const material = addGumModelToScene(geometry);
  //     addMaterial(gumModel, material, "gum");
  //   });
  // });

  // for (const attachmentModel of attachments) {
  //   const geometry = await loader.loadAsync(attachmentModel);
  //   itemsLoaded++;
  //   onProgress(itemsLoaded, itemsTotal);
  //   const material = addAttachmentModelToScene(geometry);
  //   addMaterial(attachmentModel, material, "attachments");
  // }

  // attachments.forEach((attachmentModel) => {
  //   loader.load(attachmentModel, (geometry) => {
  //     const material = addAttachmentModelToScene(geometry);
  //     addMaterial(attachmentModel, material, "attachments");
  //   });
  // });

  for (let i = 0; i < attachments.length; i = i + 6) {
    const attachmentModelsPromise = [];
    const attachmentModelChunk = [];
    for (let j = i; j < attachments.length && j < i + 6; j++) {
      attachmentModelChunk.push(attachments[j]);
      attachmentModelsPromise.push(loader.loadAsync(attachments[j]));
      itemsLoaded++;
    }

    const attachmentsGeomtry = await Promise.all(attachmentModelsPromise);

    attachmentsGeomtry.forEach((geometry, i) => {
      const { geometry: _geometry, material } =
        addAttachmentModelToScene(geometry);
      addMaterial(
        canvasId,
        attachmentModelChunk[i],
        material,
        "attachments",
        _geometry
      );
    });

    onProgress(itemsLoaded, itemsTotal);
  }
  onModelLoad(canvasId);
  cleanupGeneratedModelUrls();
};

const initModels = (canvasId) => {
  const { models } = canvasManager.get(canvasId);

  const hasUpper = Object.keys(models.UPPER || {}).length;
  const hasLower = Object.keys(models.LOWER || {}).length;
  // if (!hasUpper)
  //   document.getElementById("upper-control").style.display = "none";
  // if (!hasLower)
  //   document.getElementById("lower-control").style.display = "none";

  // if (!hasLower || !hasUpper)
  //   document.getElementById("left-controls").style.display = "none";

  const { hiddenModels, visibilityMode, superImpositions } =
    canvasManager.get(canvasId);

  Object.entries(models).forEach(([type, materials]) => {
    const keys = [...Object.keys(materials)];
    keys?.sort((a, b) => parseInt(a) - parseInt(b));
    if (type === "UPPER") {
      upperActiveNum = keys?.[0];
      if (document.getElementById("upper-active-index"))
        document.getElementById("upper-active-index").textContent =
          upperActiveNum || "";
    }

    if (type === "LOWER") {
      lowerActiveNum = keys?.[0];
      if (document.getElementById("lower-active-index"))
        document.getElementById("lower-active-index").textContent =
          lowerActiveNum || "";
    }

    if (type === "UPPER" && visibilityMode === VISIBILITY_MODES.LOWER) return;
    if (type === "LOWER" && visibilityMode === VISIBILITY_MODES.UPPER) return;

    const first = materials[keys[0]];

    if (!first?.gum) return;
    if (!hiddenModels.teeth)
      first.teeth?.forEach((material) => {
        material.visible = true;
      });
    if (!hiddenModels.attachments)
      first.attachments?.forEach((material) => {
        material.visible = true;
      });

    if (!hiddenModels.gums) first.gum.visible = true;
    superImpositions[type] = first.teeth;
  });
  canvasManager.update(canvasId, { superImpositions });
};

export const changeGumColor = (canvasId, color) => {
  const { models } = canvasManager.get(canvasId);
  Object.values(models.UPPER).forEach((model) => {
    model.gum.color = new THREE.Color(color);
  });
  Object.values(models.LOWER).forEach((model) => {
    model.gum.color = new THREE.Color(color);
  });
};

export const toggleVisibility = (canvasId, type) => {
  let { models, visibilityMode, superImpositions } =
    canvasManager.get(canvasId);

  // Diff canvas always displays aligner "00"; main canvas uses global upperActiveNum/lowerActiveNum
  const upperIdx = canvasId === "diff-canvas" ? "00" : upperActiveNum;
  const lowerIdx = canvasId === "diff-canvas" ? "00" : lowerActiveNum;
  // if (
  //   (visibilityMode === VISIBILITY_MODES.UPPER &&
  //     type === VISIBILITY_MODES.UPPER) ||
  //   (visibilityMode === VISIBILITY_MODES.LOWER &&
  //     type === VISIBILITY_MODES.LOWER)
  // ) {
  //   // toggleVisibility(canvasId, VISIBILITY_MODES.UPPER_LOWER);
  //   // return;
  //   type = VISIBILITY_MODES.UPPER_LOWER;
  // }
  [models.UPPER?.[upperIdx], models.LOWER?.[lowerIdx]].forEach(
    (m) => {
      m?.attachments?.forEach((a) => {
        a.visible = false;
      });
      m?.teeth?.forEach((t) => {
        t.visible = false;
      });
      if (m?.gum) m.gum.visible = false;
      if (m?.gumMesh) m.gumMesh.visible = false;
    }
  );

  [
    ...(superImpositions.UPPER || []),
    ...(superImpositions.LOWER || []),
  ]?.forEach((a) => {
    a.color = new THREE.Color("#fff");
    a.visible = false;
  });

  canvasManager.update(canvasId, { superImpositions });

  const toggleUpper = (canvasId) => {
    const { hiddenModels, models, superImpositions } =
      canvasManager.get(canvasId);

    if (!models.UPPER || !models.UPPER[upperIdx]) return;

    models.UPPER[upperIdx].attachments?.forEach((a) => {
      if (!hiddenModels.attachments) a.visible = true;
    });
    models.UPPER[upperIdx].teeth?.forEach((a) => {
      if (!hiddenModels.teeth) a.visible = true;
    });
    if (models.UPPER[upperIdx].gum) {
      if (!hiddenModels.gums) models.UPPER[upperIdx].gum.visible = true;
    }
    if (models.UPPER[upperIdx].gumMesh) {
      models.UPPER[upperIdx].gumMesh.visible = !hiddenModels.gums;
    }

    if (!hiddenModels.superImposition) {
      superImpositions.UPPER?.forEach((a) => {
        a.color = new THREE.Color("#fc3");
        a.visible = true;
      });
    }
    canvasManager.update(canvasId, {
      visibilityMode,
      models,
      superImpositions,
    });
  };

  const toggleLower = (canvasId) => {
    const { hiddenModels, models, superImpositions } =
      canvasManager.get(canvasId);
    if (!models.LOWER || !models.LOWER[lowerIdx]) return;
    models.LOWER[lowerIdx].attachments?.forEach((a) => {
      if (!hiddenModels.attachments) a.visible = true;
    });
    models.LOWER[lowerIdx].teeth?.forEach((a) => {
      if (!hiddenModels.teeth) a.visible = true;
    });
    if (models.LOWER[lowerIdx].gum) {
      if (!hiddenModels.gums) models.LOWER[lowerIdx].gum.visible = true;
    }
    if (models.LOWER[lowerIdx].gumMesh) {
      models.LOWER[lowerIdx].gumMesh.visible = !hiddenModels.gums;
    }
    if (!hiddenModels.superImposition) {
      superImpositions.LOWER?.forEach((a) => {
        a.color = new THREE.Color("#fc3");
        a.visible = true;
      });
    }

    canvasManager.update(canvasId, {
      visibilityMode,
      models,
      superImpositions,
    });
  };

  if (type === VISIBILITY_MODES.UPPER_LOWER) {
    visibilityMode = VISIBILITY_MODES.UPPER_LOWER;
    toggleUpper(canvasId);
    toggleLower(canvasId);
  } else if (type === VISIBILITY_MODES.UPPER) {
    visibilityMode = VISIBILITY_MODES.UPPER;
    toggleUpper(canvasId);
  } else if (type === VISIBILITY_MODES.LOWER) {
    visibilityMode = VISIBILITY_MODES.LOWER;
    toggleLower(canvasId);
  }

  canvasManager.update(canvasId, { visibilityMode, models });
};

// loadingManager.onProgress = function (url, itemsLoaded, itemsTotal) {
//   progressBar.animate(itemsLoaded / itemsTotal, { duration: 300 });
//   progressBar.setText(`${Math.ceil((itemsLoaded / itemsTotal) * 100)}%`);
// };

const getMaxUpperAlignerNumber = (canvasId) => {
  const { models } = canvasManager.get(canvasId);
  const upperKeys = Object.keys(models.UPPER || {}).map((key) =>
    parseInt(key, 10)
  );
  return Math.max(...upperKeys);
};

const getMaxLowerAlignerNumber = (canvasId) => {
  const { models } = canvasManager.get(canvasId);
  const lowerKeys = Object.keys(models.LOWER || {}).map((key) =>
    parseInt(key, 10)
  );
  return Math.max(...lowerKeys);
};

export const getMaxAlignerNumber = (canvasId) => {
  const { models } = canvasManager.get(canvasId);
  const upperKeys = Object.keys(models.UPPER || {}).map((key) =>
    parseInt(key, 10)
  );
  const lowerKeys = Object.keys(models.LOWER || {}).map((key) =>
    parseInt(key, 10)
  );

  const allKeys = [...upperKeys, ...lowerKeys];

  if (allKeys.length === 0) {
    return null;
  }

  return Math.max(...allKeys);
};

/**
 * For each arch (UPPER/LOWER), if it has fewer aligner indices than the max
 * aligner count, fill missing indices by referencing the last available model
 * for that arch (so e.g. upper with 3 aligners and max 5 will show upper[2]
 * for indices 3 and 4).
 */
const fillMissingAlignerIndices = (canvasId) => {
  const maxIdx = getMaxAlignerNumber(canvasId);
  if (maxIdx == null) return;

  const { models, modelGeometries } = canvasManager.get(canvasId);
  if (!models) return;

  const pad = (n) => String(n).padStart(2, "0");
  let realMaxUpperIndex = null;
  let realMaxLowerIndex = null;

  ["UPPER", "LOWER"].forEach((arch) => {
    const archModels = models[arch];
    if (!archModels) return;

    const keys = Object.keys(archModels)
      .map((k) => parseInt(k, 10))
      .filter((k) => !Number.isNaN(k))
      .sort((a, b) => a - b);
    if (keys.length === 0) return;

    const lastKey = keys[keys.length - 1];
    if (arch === "UPPER") realMaxUpperIndex = lastKey;
    else realMaxLowerIndex = lastKey;

    for (let i = lastKey + 1; i <= maxIdx; i++) {
      archModels[pad(i)] = archModels[pad(lastKey)];
      if (modelGeometries?.[arch]) {
        modelGeometries[arch][pad(i)] = modelGeometries[arch][pad(lastKey)];
      }
    }
  });

  canvasManager.update(canvasId, {
    models,
    ...(modelGeometries && { modelGeometries }),
    realMaxUpperIndex,
    realMaxLowerIndex,
  });
};

const onModelLoad = function (canvasId) {
  if (!sharedData.areFilesLoaded) {
    progressBar.destroy();
  }

  sharedData.areFilesLoaded = true;

  document.getElementById("loader-container").style.opacity = 0;
  setTimeout(() => {
    document.getElementById("loader-container").style.display = "none";
  }, 450);
  canvasManager.update(canvasId, {
    visibilityMode: VISIBILITY_MODES.UPPER_LOWER,
  });
  fillMissingAlignerIndices(canvasId);
  initModels(canvasId);
  const playerSlider = document.getElementById("player-slider");
  const maxAligner = getMaxAlignerNumber(canvasId);
  playerSlider.setAttribute("max", maxAligner.toString());
  playerSlider.setAttribute(
    "slider-width",
    `calc((${maxAligner} - 1) / ${maxAligner} * 100%)`
  );
  playerSlider.addCSS(`
    .range-slider {
      margin-left: auto;
      margin-right: auto;
      ${isPortrait ? "transform: translateY(-25px);" : ""}
    }
    #range-slider,
    #range-slider > .container,
    #range-slider > .container .panel,
    #range-slider > .container .panel-fill {
      height: 35px !important;
      min-height: 35px !important;
    }
    .pointer-shape{
        margin-bottom: 40px;
    }
    .panel-fill{
      background: transparent;
    }
    .panel {
      background: transparent;
    }
    .panel:hover {
      background: transparent;
    }
    
  `);
  if (isPortrait) {
    playerSlider.addCSS(`
      .tooltips {
        transform: rotate(-90deg) translateX(-30px);
        transform-origin: 0 0;
      }
        .tooltips > .tooltip {
          transform: rotate(90deg) translateX(-20px) translateY(-30px) !important;
        }
        #range-slider > .container {
          transform: rotate(-90deg);
          transform-origin: 0 0;
        }
      `);

    setTimeout(() => {
      const sliderElement = document.getElementsByTagName("tc-range-slider")[0];

      if (sliderElement && sliderElement.shadowRoot) {
        // Get the vertical slider box dimensions
        const verticalBox =
          sliderElement.shadowRoot.querySelector("#range-slider");

        if (verticalBox) {
          const boxWidth = verticalBox.clientWidth;
          const boxHeight = verticalBox.clientHeight;

          // Get container and tooltips elements
          const elements = sliderElement.shadowRoot.querySelectorAll(
            "#range-slider > .container, .tooltips"
          );

          // Update dimensions
          if (elements.length > 0) {
            elements[0].style.height = `${boxWidth}px`;
            elements[0].style.width = `40px`;
          }

          if (elements.length > 1) {
            elements[1].style.width = `${boxHeight}px`;
            elements[1].style.height = `40px`;
          }
        }
      }
    }, 1000);
  }

  function createStripedBackground(elementId, alignerCount, maxAlignerCount) {
    const element = document.getElementById(elementId);
    if (!element) return;

    element.querySelectorAll(".stripe-segment").forEach((el) => el.remove());
    element.style.background = "none";

    const stripeWidthExpr = `(100% - 5px * ${maxAlignerCount - 1}) / ${maxAlignerCount}`;

    for (let i = 0; i < maxAlignerCount; i++) {
      const opacity = i < alignerCount ? 1 : 0.3;
      // 0th strip: #434b5a; others: blue (#6fc3df)
      const color =
        i === 0
          ? `rgba(67, 75, 90, ${opacity})`
          : `rgba(111, 195, 223, ${opacity})`;

      const left = i === 0
        ? "0px"
        : `calc(${i} * (${stripeWidthExpr} + 5px))`;

      const stripe = document.createElement("div");
      stripe.className = "stripe-segment";
      stripe.style.cssText = `
        position: absolute;
        top: 0;
        left: ${left};
        width: calc(${stripeWidthExpr});
        height: 100%;
        background: ${color};
        pointer-events: none;
      `;
      element.appendChild(stripe);
    }
  }

  // Center of stripe to align with createStripedBackground gradient. Slider/stripes use aligner number as index (aligner 0 = stripe 0, aligner 1 = stripe 1).
  function getStripeCenterLeft(aligner, maxAlignerCount) {
    const stripeIdx = Math.max(0, Math.min(aligner, maxAlignerCount - 1));
    const stripeWidth = `(100% - 5px * (${maxAlignerCount} - 1)) / ${maxAlignerCount}`;
    return `calc(${stripeIdx} * (${stripeWidth} + 5px) + ${stripeWidth} / 2)`;
  }

  function createIPRStartDiamonds(elementId, maxAlignerCount, archActiveCount) {
    const element = document.getElementById(elementId);
    if (!element) return;

    // Remove existing diamonds
    element.querySelectorAll(".ipr-start-diamond").forEach((el) => el.remove());

    const arch = elementId === "upper-track" ? "upper" : "lower";
    const cap = archActiveCount != null ? archActiveCount : maxAlignerCount;
    const startAligners = getIPRStartAlignerNumbers(arch).filter((a) => a < cap);
    if (startAligners.length === 0) return;

    const diamondSize = isPortrait ? 6 : 8;
    startAligners.forEach((startAligner) => {
      const diamond = document.createElement("span");
      diamond.className = "ipr-start-diamond";
      const left = getStripeCenterLeft(startAligner, maxAlignerCount);
      diamond.style.cssText = `
        position: absolute;
        left: ${left};
        top: 50%;
        transform: translate(-50%, -50%) rotate(45deg);
        width: ${diamondSize}px;
        height: ${diamondSize}px;
        background: #FFD700;
        pointer-events: none;
        z-index: 3;
      `;
      element.appendChild(diamond);
    });
  }

  function createAttachmentMarkers(elementId, maxAlignerCount, archActiveCount) {
    const element = document.getElementById(elementId);
    if (!element) return;

    element.querySelectorAll(".attachment-start-marker").forEach((el) => el.remove());
    element.querySelectorAll(".attachment-end-marker").forEach((el) => el.remove());

    const arch = elementId === "upper-track" ? "upper" : "lower";
    const cap = archActiveCount != null ? archActiveCount : maxAlignerCount;
    // Use maxAlignerCount so the end-of-treatment removal marker is placed at the
    // global last step for both arches, even when one arch has fewer aligners.
    const { starts, ends } = getAttachmentLayerMarkers(maxAlignerCount, arch);
    const activeStarts = starts.filter((a) => a < cap);
    // Mid-treatment removals are capped to the arch's active range (< cap).
    // End-of-treatment removal (=== maxAlignerCount) is always shown for both arches.
    const activeEnds = ends.filter((a) => a < cap || a === maxAlignerCount);

    const markerSize = isPortrait ? 10 : 14;
    const halfSize = markerSize / 2;

    // Up arrow at layer start — same stripe center as IPR
    activeStarts.forEach((aligner) => {
      const arrow = document.createElement("span");
      arrow.className = "attachment-start-marker";
      const left = getStripeCenterLeft(aligner, maxAlignerCount);
      arrow.style.cssText = `
        position: absolute;
        left: ${left};
        top: 42%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-left: ${halfSize}px solid transparent;
        border-right: ${halfSize}px solid transparent;
        border-bottom: ${markerSize}px solid #ff7078;
        pointer-events: none;
        z-index: 1;
      `;
      element.appendChild(arrow);
    });

    // Down arrow at layer end — same stripe center as IPR/start
    activeEnds.forEach((aligner) => {
      const arrow = document.createElement("span");
      arrow.className = "attachment-end-marker";
      const left = getStripeCenterLeft(aligner, maxAlignerCount);
      arrow.style.cssText = `
        position: absolute;
        left: ${left};
        top: 50%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-left: ${halfSize}px solid transparent;
        border-right: ${halfSize}px solid transparent;
        border-top: ${markerSize}px solid #ff7078;
        pointer-events: none;
        z-index: 1;
      `;
      element.appendChild(arrow);
    });
  }

  const { realMaxUpperIndex, realMaxLowerIndex } =
    canvasManager.get(canvasId) || {};
  const upperActiveCount = Math.max(
    0,
    (realMaxUpperIndex != null
      ? realMaxUpperIndex
      : getMaxUpperAlignerNumber(canvasId)) + 1
  );
  const lowerActiveCount = Math.max(
    0,
    (realMaxLowerIndex != null
      ? realMaxLowerIndex
      : getMaxLowerAlignerNumber(canvasId)) + 1
  );
  createStripedBackground("upper-track", upperActiveCount, maxAligner + 1);
  createStripedBackground("lower-track", lowerActiveCount, maxAligner + 1);
  const maxAlignerCount = maxAligner + 1;
  createIPRStartDiamonds("upper-track", maxAlignerCount, upperActiveCount);
  createIPRStartDiamonds("lower-track", maxAlignerCount, lowerActiveCount);
  createAttachmentMarkers("upper-track", maxAlignerCount, upperActiveCount);
  createAttachmentMarkers("lower-track", maxAlignerCount, lowerActiveCount);
  if (canvasId === "canvas") {
    const upperEl = document.querySelector("#aligner-labels > #upper-aligner-label");
    const lowerEl = document.querySelector("#aligner-labels > #lower-aligner-label");
    if (upperEl && realMaxUpperIndex != null)
      upperEl.textContent = String(realMaxUpperIndex).padStart(2, "0");
    if (lowerEl && realMaxLowerIndex != null)
      lowerEl.textContent = String(realMaxLowerIndex).padStart(2, "0");
  }
  changeActiveIndexByValue(canvasId, 0);

  // Initialize IPR visualization after models are loaded (skip diff canvas)
  if (canvasId !== "diff-canvas") {
    setTimeout(() => {
      initializeIPRVisualization(canvasId, upperActiveNum, lowerActiveNum);
    }, 500);
  }
};

const hideModel = (canvasId, alignerType, index) => {
  const { models, hiddenModels, visibilityMode, superImpositions } =
    canvasManager.get(canvasId);
  const _models = models[alignerType.toUpperCase()];
  const model = _models[index];
  if (!model) return;

  if (model.gum) model.gum.visible = false;
  if (model.gumMesh) model.gumMesh.visible = false;
  model.teeth?.forEach((material) => {
    material.visible = false;
  });
  model.attachments?.forEach((material) => {
    material.visible = false;
  });
  // Only show superImposition for the active arch (respect upper-only / lower-only mode)
  const archActive =
    visibilityMode === VISIBILITY_MODES.UPPER_LOWER ||
    (visibilityMode === VISIBILITY_MODES.UPPER && alignerType === "upper") ||
    (visibilityMode === VISIBILITY_MODES.LOWER && alignerType === "lower");
  if (!hiddenModels.superImposition && archActive) {
    superImpositions[alignerType.toUpperCase()]?.forEach((a) => {
      a.color = new THREE.Color("#fc3");
      a.visible = true;
    });
  }
  canvasManager.update(canvasId, {
    models,
    superImpositions,
  });
};

const showModel = (canvasId, alignerType, index) => {
  const { models, hiddenModels, visibilityMode } = canvasManager.get(canvasId);
  if (visibilityMode === VISIBILITY_MODES.LOWER && alignerType === "upper")
    return;
  if (visibilityMode === VISIBILITY_MODES.UPPER && alignerType === "lower")
    return;
  const _models = models[alignerType.toUpperCase()];
  const model = _models[index];
  if (!hiddenModels.gums && model.gum) model.gum.visible = true;
  if (model.gumMesh) model.gumMesh.visible = !hiddenModels.gums;
  if (!hiddenModels.teeth)
    model.teeth?.forEach((material) => {
      material.visible = true;
    });
  if (!hiddenModels.attachments)
    model.attachments?.forEach((material) => {
      material.visible = true;
    });
};

const getNewIndex = (canvasId, currIndex, alignerType, controlType) => {
  const { models } = canvasManager.get(canvasId);
  const keys = Object.keys(models[alignerType.toUpperCase()]);
  keys?.sort((a, b) => Number(a) - Number(b));
  const allIndices = [...keys];
  const i = allIndices.indexOf(currIndex);
  return controlType === "prev" ? allIndices[i - 1] : allIndices[i + 1];
};

export const changeActiveIndex = (canvasId, alignerType, controlType) => {
  const { models } = canvasManager.get(canvasId);
  const index = alignerType === "upper" ? upperActiveNum : lowerActiveNum;
  const _models = models[alignerType.toUpperCase()];
  if (!_models) return;
  const newIdx = getNewIndex(canvasId, index, alignerType, controlType);
  if (!newIdx) return;
  const model = _models?.[newIdx];
  if (!model) return;
  hideModel(canvasId, alignerType, index);
  showModel(canvasId, alignerType, newIdx);
  if (alignerType === "upper") {
    upperActiveNum = newIdx;
  } else {
    lowerActiveNum = newIdx;
  }
  
  // Update IPR visualization positions when aligner index changes
  if (canvasId === "canvas") {
    updateIPRPositions(canvasId, upperActiveNum, lowerActiveNum);
  }
  
  return newIdx;
};

const delay = async (time) => {
  return new Promise((resolve) => setTimeout(resolve, time));
};

let isPlaying = false;

export const play = async (canvasId) => {
  const playControl = document.getElementById("play");
  const playerSlider = document.getElementById("player-slider");
  if (isPlaying) {
    isPlaying = false;
    playerSlider.disabled = false;
    playControl.classList.remove("icon-pause");
    playControl.classList.add("icon-play_back");
    return;
  }
  isPlaying = true;
  playControl.classList.remove("icon-play_back");
  playControl.classList.add("icon-pause");

  const maxAligner = getMaxAlignerNumber(canvasId);

  let currentValue = Math.max(Number(lowerActiveNum), Number(upperActiveNum));
  // When at last aligner, wrap to 0 so play continues from the start
  if (currentValue >= maxAligner) {
    currentValue = 0;
  }
  playerSlider.disabled = true;

  for (let i = currentValue; i <= maxAligner; i++) {
    if (!isPlaying) break;
    playerSlider.value = i;
    canvasManager.getAll().forEach((canvas) => {
      changeActiveIndexByValue(canvas.id, i);
    });
    await delay(340);
  }

  isPlaying = false;
  playerSlider.disabled = false;
  playControl.classList.remove("icon-pause");
  playControl.classList.add("icon-play_back");
};

export const toggleModelVisibility = (canvasId, type) => {
  const { hiddenModels, visibilityMode } = canvasManager.get(canvasId);
  hiddenModels[type] = !hiddenModels[type];

  toggleVisibility(canvasId, visibilityMode);

  canvasManager.update(canvasId, { hiddenModels });
};

/** Set model visibility for one canvas (used to keep all canvases in sync). */
export const setModelVisibility = (canvasId, type, hidden) => {
  const { hiddenModels, visibilityMode } = canvasManager.get(canvasId);
  hiddenModels[type] = hidden;
  toggleVisibility(canvasId, visibilityMode);
  canvasManager.update(canvasId, { hiddenModels });
};

export const changeActiveIndexByValue = (canvasId, value) => {
  if (canvasId === "diff-canvas") {
    value = 0;
  }
  const { models } = canvasManager.get(canvasId);
  const indexString = value.toString().padStart(2, "0");
  const showAlignerModel = (alignerType, index) => {
    const _models = models[alignerType.toUpperCase()];
    if (!_models) return;
    const model = _models[index];
    if (!model) return;

    Object.keys(_models).forEach((key) => {
      hideModel(canvasId, alignerType, key);
    });

    showModel(canvasId, alignerType, index);
  };

  showAlignerModel("upper", indexString);
  showAlignerModel("lower", indexString);

  // When only one arch is active, hide the inactive arch's superImposition
  const { visibilityMode, hiddenModels, superImpositions } =
    canvasManager.get(canvasId);
  if (!hiddenModels.superImposition) {
    if (visibilityMode === VISIBILITY_MODES.UPPER) {
      superImpositions.LOWER?.forEach((a) => {
        a.color = new THREE.Color("#fff");
        a.visible = false;
      });
    } else if (visibilityMode === VISIBILITY_MODES.LOWER) {
      superImpositions.UPPER?.forEach((a) => {
        a.color = new THREE.Color("#fff");
        a.visible = false;
      });
    }
    canvasManager.update(canvasId, { superImpositions });
  }

  // Update global upperActiveNum/lowerActiveNum only for the main canvas so that
  // toggleVisibility and other code that uses these globals affect the correct
  // aligner on the main canvas. Do not update for diff-canvas, otherwise the
  // global state would become "00" and cause overlapping teeth on the main canvas
  // when gums/visibility are toggled (they would touch index "00" on main while
  // main is displaying a different aligner).
  if (canvasId === "canvas") {
    if (indexString) {
      upperActiveNum = indexString;
      lowerActiveNum = indexString;
    }
  }
  
  // Update IPR visualization positions when aligner index changes (skip diff canvas)
  if (canvasId !== "diff-canvas") {
    updateIPRPositions(canvasId, upperActiveNum, lowerActiveNum);
  }
};

export const handleDiffView = () => {
  // sharedData.currentView = VIEWS.DIFF;
  // const mainCanvas = canvasManager.get("canvas");
  // const diffCanvas = canvasManager.get("diff-canvas");
  // const slider = document.getElementById("player-slider");
  // slider.value = 0;
};

const updatePdfLinkModalContent = (pdfLinks) => {
  const pdfFilesContainer = document.getElementById("pdf-files-container");
  if (!pdfFilesContainer) return;

  const links = pdfLinks && typeof pdfLinks === "object" ? pdfLinks : {};
  pdfFilesContainer.innerHTML = "";

  const pdfTypeConfig = {
    TREATMENT_PLAN: { label: "Treatment Plan PDF", icon: "data" },
    // IPR_APPOINTMENT_1: { label: "IPR Appointment 1", icon: "deglazing" },
    // IPR_APPOINTMENT_2: { label: "IPR Appointment 2", icon: "deglazing" },
    // ATTACHMENTS_APPOINTMENT_1: { label: "Attachments Appointment 1", icon: "attachments" },
    // ATTACHMENTS_APPOINTMENT_2: { label: "Attachments Appointment 2", icon: "attachments" },
    // MOVEMENT: { label: "Movement Overview", icon: "movement" },
  };

  Object.entries(links).forEach(([type, url]) => {
    if (url && pdfTypeConfig[type]) {
      const config = pdfTypeConfig[type];

      const linkElement = document.createElement("a");
      linkElement.href = url;
      linkElement.target = "_blank";
      linkElement.className = "pdf-icon-link";

      const iconElement = document.createElement("i");
      iconElement.className = `iconfont icon-${config.icon}`;

      const labelElement = document.createElement("span");
      labelElement.textContent = config.label;

      linkElement.appendChild(iconElement);
      linkElement.appendChild(labelElement);

      pdfFilesContainer.appendChild(linkElement);
    }
  });

  if (pdfFilesContainer.children.length === 0) {
    const emptyEl = document.createElement("p");
    emptyEl.className = "pdf-files-empty";
    emptyEl.textContent = "No PDF files available.";
    pdfFilesContainer.appendChild(emptyEl);
  }
};
